import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { SaveIcon, ImageIcon, TagIcon, XIcon, PlusIcon, CheckIcon, EyeIcon, ArrowLeftIcon, StarIcon, CalendarIcon, ClockIcon } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { getArticleById, createArticle, updateArticle, uploadArticleImage, Article } from '../../services/articles';
import { getAllCategories, Category } from '../../services/categories';
import RichTextEditor from './RichTextEditor';
import { Timestamp } from 'firebase/firestore';
const ArticleEditor: React.FC = () => {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const {
    user
  } = useAuth();
  const navigate = useNavigate();
  const [article, setArticle] = useState<Partial<Article>>({
    title: '',
    content: '',
    excerpt: '',
    featuredImage: '',
    tags: [],
    categoryId: '',
    categoryName: '',
    status: 'draft',
    featured: false
  });
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(id ? true : false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [newTag, setNewTag] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Scheduling states
  const [isScheduled, setIsScheduled] = useState(false);
  const [scheduledDate, setScheduledDate] = useState<string>('');
  const [scheduledTime, setScheduledTime] = useState<string>('');
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch categories
        const fetchedCategories = await getAllCategories();
        setCategories(fetchedCategories);
        // If editing an existing article, fetch it
        if (id) {
          const fetchedArticle = await getArticleById(id);
          if (fetchedArticle) {
            setArticle(fetchedArticle);
            // Set scheduling fields if article is scheduled
            if (fetchedArticle.scheduledFor) {
              const scheduledDate = fetchedArticle.scheduledFor.toDate();
              setIsScheduled(true);
              setScheduledDate(formatDateForInput(scheduledDate));
              setScheduledTime(formatTimeForInput(scheduledDate));
            }
          } else {
            setError('Article not found');
          }
        }
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load data. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const {
      name,
      value
    } = e.target;
    setArticle(prev => ({
      ...prev,
      [name]: value
    }));
    // If changing category, also update categoryName
    if (name === 'categoryId' && value) {
      const selectedCategory = categories.find(cat => cat.id === value);
      if (selectedCategory) {
        setArticle(prev => ({
          ...prev,
          categoryName: selectedCategory.name
        }));
      }
    }
  };
  const handleContentChange = (content: string) => {
    setArticle(prev => ({
      ...prev,
      content
    }));
  };
  const handleFeaturedToggle = () => {
    setArticle(prev => ({
      ...prev,
      featured: !prev.featured
    }));
  };
  const handleScheduleToggle = () => {
    setIsScheduled(!isScheduled);
    // If turning off scheduling, clear the scheduledFor field
    if (isScheduled) {
      setArticle(prev => ({
        ...prev,
        scheduledFor: undefined
      }));
    }
  };
  const handleAddTag = () => {
    if (!newTag.trim()) return;
    setArticle(prev => ({
      ...prev,
      tags: [...(prev.tags || []), newTag.trim()]
    }));
    setNewTag('');
  };
  const handleRemoveTag = (tagToRemove: string) => {
    setArticle(prev => ({
      ...prev,
      tags: (prev.tags || []).filter(tag => tag !== tagToRemove)
    }));
  };
  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    try {
      setLoading(true);
      // Use the article ID if it exists, otherwise use a temporary ID
      const articleId = id || 'temp-' + Date.now();
      const imageUrl = await uploadArticleImage(files[0], articleId);
      setArticle(prev => ({
        ...prev,
        featuredImage: imageUrl
      }));
      // Reset the file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (err) {
      console.error('Error uploading image:', err);
      setError('Failed to upload image. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  const handleSave = async (newStatus?: 'draft' | 'published' | 'archived') => {
    if (!user) return;
    try {
      setSaving(true);
      // Validate required fields
      if (!article.title?.trim()) {
        setError('Title is required');
        return;
      }
      if (!article.excerpt?.trim()) {
        setError('Excerpt is required');
        return;
      }
      if (!article.content?.trim()) {
        setError('Content is required');
        return;
      }
      // Validate scheduling if enabled
      if (isScheduled && (newStatus === 'published' || article.status === 'published')) {
        if (!scheduledDate || !scheduledTime) {
          setError('Please set both date and time for scheduling');
          return;
        }
        const scheduledDateTime = new Date(`${scheduledDate}T${scheduledTime}`);
        if (isNaN(scheduledDateTime.getTime())) {
          setError('Invalid date or time format');
          return;
        }
        if (scheduledDateTime <= new Date()) {
          setError('Scheduled time must be in the future');
          return;
        }
        // Set the scheduledFor field
        article.scheduledFor = Timestamp.fromDate(scheduledDateTime);
      } else if (!isScheduled) {
        article.scheduledFor = undefined;
      }
      const status = newStatus || article.status || 'draft';
      if (id) {
        // Update existing article
        await updateArticle(id, {
          ...article,
          status
        });
        setSuccessMessage(`Article ${status === 'published' ? isScheduled ? 'scheduled' : 'published' : 'saved'} successfully!`);
      } else {
        // Create new article
        const newArticle = await createArticle({
          ...article,
          status
        }, user);
        setSuccessMessage(`Article created successfully!`);
        // Redirect to edit page with the new ID
        if (newArticle.id) {
          setTimeout(() => {
            navigate(`/dashboard/articles/edit/${newArticle.id}`);
          }, 1500);
        }
      }
    } catch (err) {
      console.error('Error saving article:', err);
      setError('Failed to save article. Please try again.');
    } finally {
      setSaving(false);
      // Clear success message after a delay
      if (successMessage) {
        setTimeout(() => {
          setSuccessMessage(null);
        }, 5000);
      }
    }
  };
  // Format date for date input (YYYY-MM-DD)
  const formatDateForInput = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  // Format time for time input (HH:MM)
  const formatTimeForInput = (date: Date): string => {
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${hours}:${minutes}`;
  };
  // Get current date and time for min attribute on date picker
  const getTodayForDateInput = (): string => {
    return formatDateForInput(new Date());
  };
  if (loading && id) {
    return <div className="flex justify-center py-12" data-id="element-114">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-navy" data-id="element-115"></div>
      </div>;
  }
  return <div data-id="element-116">
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-6" data-id="element-117">
        <div className="flex items-center" data-id="element-118">
          <button onClick={() => navigate('/dashboard/articles')} className="mr-3 text-gray-600 hover:text-navy" data-id="element-119">
            <ArrowLeftIcon size={20} data-id="element-120" />
          </button>
          <h1 className="text-2xl font-bold text-navy font-montserrat" data-id="element-121">
            {id ? 'Edit Article' : 'Create New Article'}
          </h1>
        </div>
        <div className="flex space-x-3 mt-4 md:mt-0" data-id="element-122">
          <button onClick={() => handleSave('draft')} className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 flex items-center" disabled={saving} data-id="element-123">
            <SaveIcon size={18} className="mr-2" data-id="element-124" />
            Save Draft
          </button>
          <button onClick={() => handleSave('published')} className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 flex items-center" disabled={saving} data-id="element-125">
            <EyeIcon size={18} className="mr-2" data-id="element-126" />
            {article.status === 'published' ? isScheduled ? 'Update Schedule' : 'Update Published' : isScheduled ? 'Schedule' : 'Publish'}
          </button>
        </div>
      </div>
      {error && <div className="bg-red-50 text-red-700 p-4 rounded-md mb-6" data-id="element-127">
          {error}
        </div>}
      {successMessage && <div className="bg-green-50 text-green-700 p-4 rounded-md mb-6 flex items-center" data-id="element-128">
          <CheckIcon size={18} className="mr-2" data-id="element-129" />
          {successMessage}
        </div>}
      <div className="bg-white rounded-lg shadow-sm p-6" data-id="element-130">
        <div className="mb-6" data-id="element-131">
          <label htmlFor="title" className="block mb-2 font-medium" data-id="element-132">
            Title *
          </label>
          <input type="text" id="title" name="title" value={article.title} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange" placeholder="Enter article title" required data-id="element-133" />
        </div>
        <div className="mb-6" data-id="element-134">
          <label htmlFor="excerpt" className="block mb-2 font-medium" data-id="element-135">
            Excerpt / Summary *
          </label>
          <textarea id="excerpt" name="excerpt" value={article.excerpt} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange" placeholder="Brief summary of the article" rows={3} required data-id="element-136" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6" data-id="element-137">
          <div data-id="element-138">
            <label htmlFor="categoryId" className="block mb-2 font-medium" data-id="element-139">
              Category
            </label>
            <select id="categoryId" name="categoryId" value={article.categoryId} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange" data-id="element-140">
              <option value="" data-id="element-141">Select a category</option>
              {categories.map(category => <option key={category.id} value={category.id} data-id="element-142">
                  {category.name}
                </option>)}
            </select>
          </div>
          <div data-id="element-143">
            <label className="block mb-2 font-medium" data-id="element-144">Featured Article</label>
            <div className="flex items-center" data-id="element-145">
              <button type="button" onClick={handleFeaturedToggle} className={`flex items-center px-4 py-2 rounded-md ${article.featured ? 'bg-gold text-navy' : 'bg-gray-200 text-gray-700'}`} data-id="element-146">
                <StarIcon size={18} className={`mr-2 ${article.featured ? 'fill-navy' : ''}`} data-id="element-147" />
                {article.featured ? 'Featured' : 'Not Featured'}
              </button>
              <span className="ml-3 text-sm text-gray-500" data-id="element-148">
                Featured articles appear in the highlights section
              </span>
            </div>
          </div>
        </div>
        {/* Scheduling Options */}
        <div className="mb-6 p-4 bg-gray-50 rounded-lg" data-id="element-149">
          <div className="flex items-center mb-4" data-id="element-150">
            <button type="button" onClick={handleScheduleToggle} className={`flex items-center px-4 py-2 rounded-md mr-3 ${isScheduled ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-700'}`} data-id="element-151">
              <CalendarIcon size={18} className="mr-2" data-id="element-152" />
              {isScheduled ? 'Scheduled' : 'Schedule for Later'}
            </button>
            <span className="text-sm text-gray-500" data-id="element-153">
              {isScheduled ? 'Article will be published at the scheduled time' : 'Publish immediately or schedule for later'}
            </span>
          </div>
          {isScheduled && <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-154">
              <div data-id="element-155">
                <label htmlFor="scheduledDate" className="block mb-2 text-sm font-medium" data-id="element-156">
                  Publication Date *
                </label>
                <input type="date" id="scheduledDate" value={scheduledDate} onChange={e => setScheduledDate(e.target.value)} min={getTodayForDateInput()} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange" required={isScheduled} data-id="element-157" />
              </div>
              <div data-id="element-158">
                <label htmlFor="scheduledTime" className="block mb-2 text-sm font-medium" data-id="element-159">
                  Publication Time *
                </label>
                <input type="time" id="scheduledTime" value={scheduledTime} onChange={e => setScheduledTime(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange" required={isScheduled} data-id="element-160" />
              </div>
            </div>}
        </div>
        <div className="mb-6" data-id="element-161">
          <label className="block mb-2 font-medium" data-id="element-162">Featured Image</label>
          {article.featuredImage ? <div className="mb-4" data-id="element-163">
              <div className="relative" data-id="element-164">
                <img src={article.featuredImage} alt="Featured" className="w-full max-h-64 object-cover rounded-md" data-id="element-165" />
                <button onClick={() => setArticle(prev => ({
              ...prev,
              featuredImage: ''
            }))} className="absolute top-2 right-2 bg-red-600 text-white p-1 rounded-full hover:bg-red-700" data-id="element-166">
                  <XIcon size={16} data-id="element-167" />
                </button>
              </div>
            </div> : <div className="border-2 border-dashed border-gray-300 rounded-md p-8 text-center cursor-pointer hover:bg-gray-50" onClick={() => fileInputRef.current?.click()} data-id="element-168">
              <ImageIcon size={36} className="mx-auto text-gray-400 mb-2" data-id="element-169" />
              <p className="text-gray-500" data-id="element-170">Click to upload a featured image</p>
              <input type="file" ref={fileInputRef} onChange={handleImageUpload} accept="image/*" className="hidden" data-id="element-171" />
            </div>}
        </div>
        <div className="mb-6" data-id="element-172">
          <label htmlFor="content" className="block mb-2 font-medium" data-id="element-173">
            Content *
          </label>
          <RichTextEditor content={article.content || ''} onChange={handleContentChange} data-id="element-174" />
          {article.content && <div className="mt-2 text-sm text-gray-500 flex items-center" data-id="element-175">
              <ClockIcon size={14} className="mr-1" data-id="element-176" />
              Estimated reading time: {article.readTime || 3} minutes
            </div>}
        </div>
        <div className="mb-6" data-id="element-177">
          <label className="block mb-2 font-medium" data-id="element-178">Tags</label>
          <div className="flex flex-wrap gap-2 mb-3" data-id="element-179">
            {article.tags && article.tags.map((tag, index) => <span key={index} className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-800" data-id="element-180">
                  {tag}
                  <button onClick={() => handleRemoveTag(tag)} className="ml-2 text-gray-500 hover:text-gray-700" data-id="element-181">
                    <XIcon size={14} data-id="element-182" />
                  </button>
                </span>)}
          </div>
          <div className="flex" data-id="element-183">
            <input type="text" value={newTag} onChange={e => setNewTag(e.target.value)} onKeyPress={e => e.key === 'Enter' && handleAddTag()} className="flex-1 px-3 py-2 border border-gray-300 rounded-l-md focus:outline-none focus:ring-2 focus:ring-orange" placeholder="Add a tag" data-id="element-184" />
            <button onClick={handleAddTag} className="bg-navy text-white px-3 py-2 rounded-r-md hover:bg-navy-800" data-id="element-185">
              <PlusIcon size={18} data-id="element-186" />
            </button>
          </div>
        </div>
        <div className="flex justify-end space-x-3" data-id="element-187">
          <button onClick={() => navigate('/dashboard/articles')} className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50" data-id="element-188">
            Cancel
          </button>
          <button onClick={() => handleSave()} className="px-4 py-2 bg-orange text-white rounded-md hover:bg-orange-600 flex items-center" disabled={saving} data-id="element-189">
            <SaveIcon size={18} className="mr-2" data-id="element-190" />
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>;
};
export default ArticleEditor;